/**
 * RESPONSIBILITY: send one file to the upload endpoint and return what it stored.
 *
 * Shared because every control that uploads — a picture, a picture inside a row, a video —
 * speaks the same HTTP contract, and one contract should have one home.
 *
 * Nothing here says where the file is going. The server names it after a fresh id, so an
 * upload no longer belongs to a slot — which is what stopped a reordered list from moving
 * its pictures.
 *
 * `kind` is what the FIELD is, not what the file claims to be. The server keeps a separate
 * allow-list per kind, so a picture slot cannot be filled with a video by accident and a
 * video slot cannot be handed an SVG.
 */
import { FRAMEWORK_ROUTES, ApiError, Ok, Err, type ImageRecord, type Result } from '@framework/core';

export type UploadKind = 'image' | 'video';

export async function uploadMedia(
  file: File, folder: string, maxSizeMb: number, kind: UploadKind = 'image',
): Promise<Result<ImageRecord>> {
  const fd = new FormData();
  fd.append('file', file);
  fd.append('maxSizeMb', String(maxSizeMb));
  fd.append('kind', kind);

  const res = await fetch(FRAMEWORK_ROUTES.upload(folder), {
    method: 'POST', body: fd, credentials: 'include',
  });
  const body = await res.json();

  if (!res.ok) {
    return Err(
      body.error === ApiError.TooLarge
        ? `Too large — max ${maxSizeMb} MB`
        : body.message ?? 'Upload failed',
    );
  }

  return Ok(body as ImageRecord);
}
